import type { HttpsConfig } from '@ms-cloudpack/common-types';
import { parseHttpsConfig } from '@ms-cloudpack/create-express-app';
import { makeUrl } from '@ms-cloudpack/path-string-parsing';
import type { AnyRouter } from '@trpc/server';
import {
  nodeHTTPRequestHandler,
  type NodeHTTPCreateContextFnOptions,
  type NodeHTTPHandlerOptions,
} from '@trpc/server/adapters/node-http';
import http from 'http';
import https from 'https';

const defaultDomain = 'localhost';

export type CreateHTTPHandlerOptions<TRouter extends AnyRouter> = NodeHTTPHandlerOptions<
  TRouter,
  http.IncomingMessage,
  http.ServerResponse
> & { https?: true | HttpsConfig; cwd: string };

export type CreateHTTPContextOptions = NodeHTTPCreateContextFnOptions<http.IncomingMessage, http.ServerResponse>;

export function createHTTPHandler<TRouter extends AnyRouter>(
  options: CreateHTTPHandlerOptions<TRouter> & { domain?: string },
) {
  return async (req: http.IncomingMessage, res: http.ServerResponse): Promise<void> => {
    // if no hostname, set a dummy one so that we can consistently parse the pathname.
    const url = req.url as string;
    const { domain = defaultDomain } = options;

    const href = url.startsWith('/') ? `http://${domain}${url}` : url;

    // get procedure path and remove the leading slash
    // /procedure -> procedure
    const path = makeUrl(href).pathname.slice(1);

    await nodeHTTPRequestHandler({
      ...options,
      req,
      res,
      path,
    });
  };
}

export async function createHTTPServer<TRouter extends AnyRouter>(
  options: CreateHTTPHandlerOptions<TRouter> & { domain?: string },
): Promise<{
  server: http.Server | https.Server;
  listen: (port: number, host?: string) => Promise<{ port: number; close: () => void }>;
}> {
  const handler = createHTTPHandler(options);
  const { domain = defaultDomain, https: httpsOptions } = options;

  let server: http.Server | https.Server;

  if (httpsOptions) {
    const httpsConfig = await parseHttpsConfig({ domain, https: httpsOptions, cwd: options.cwd });

    const { ca, key, cert, passphrase } = httpsConfig || {};

    if (!httpsConfig || !key || !cert) {
      console.error('Invalid https config in cloudpack.config.json. Make sure key and cert are valid.');

      throw new Error('Invalid https config.');
    }

    server = https.createServer(
      {
        ca,
        key,
        cert,
        passphrase,
      },
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      handler,
    );
  } else {
    server = http.createServer(
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      handler,
    );
  }

  return {
    server,
    listen: async (port: number, host?: string) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
      const actualPort = port === 0 ? ((server.address() as any).port as number) : port;

      return new Promise((resolve, reject) => {
        server.addListener('listening', () => {
          resolve({
            port: actualPort,
            close: () => {
              server.close();
            },
          });
        });

        server.addListener('error', (err) => {
          reject(err);
        });

        server.listen(port, host);
      });
    },
  };
}
